import api, { BASE_URL } from './api';

/**
 * Event interface matching backend entity
 */
export interface Evenement {
    id: string;
    titre: string;
    description?: string;
    lienInscription?: string;
    pdfProgramme?: string;
    image?: string;
    dateDebut: string; // ISO date string
    dateFin: string; // ISO date string
    dateSoumission: string; // ISO date string
    isCallForPapers: boolean;
    isPublishedOnHome: boolean;
    nombreVues: number;
    nombreLikes: number;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
}

/**
 * DTO for creating an event
 */
export interface CreateEvenementDto {
    titre: string;
    description?: string;
    lienInscription?: string;
    dateDebut: string; // YYYY-MM-DD format
    dateFin: string; // YYYY-MM-DD format
    dateSoumission?: string; // YYYY-MM-DD format
    isCallForPapers?: boolean;
    isPublishedOnHome?: boolean;
    image?: File;
    pdfProgramme?: File;
}

/**
 * DTO for updating an event
 */
export interface UpdateEvenementDto {
    titre?: string;
    description?: string;
    lienInscription?: string;
    dateDebut?: string;
    dateFin?: string;
    dateSoumission?: string;
    isCallForPapers?: boolean;
    isPublishedOnHome?: boolean;
    image?: File;
    pdfProgramme?: File;
}

/**
 * Paginated response structure
 */
export interface PaginatedEvenements {
    data: Evenement[];
    total: number;
    page: number;
    limit: number;
}

/**
 * Helper function to fix file URLs
 */
const fixFileUrl = (url: string | undefined): string | undefined => {
    if (!url) return url;
    if (url.startsWith('http')) return url;
    return `${BASE_URL}${url}`;
};

/**
 * Fix evenement file URLs
 */
const fixEvenement = (evenement: Evenement): Evenement => ({
    ...evenement,
    image: fixFileUrl(evenement.image),
    pdfProgramme: fixFileUrl(evenement.pdfProgramme),
});

/**
 * Evenements API Service
 */
export const EvenementsService = {
    /**
     * Create a new event with file uploads
     */
    create: async (data: CreateEvenementDto): Promise<Evenement> => {
        const formData = new FormData();

        // Append text fields
        formData.append('titre', data.titre);
        if (data.description) formData.append('description', data.description);
        if (data.lienInscription) formData.append('lienInscription', data.lienInscription);
        formData.append('dateDebut', data.dateDebut);
        formData.append('dateFin', data.dateFin);
        if (data.dateSoumission) formData.append('dateSoumission', data.dateSoumission);
        if (data.isCallForPapers !== undefined) formData.append('isCallForPapers', String(data.isCallForPapers));
        if (data.isPublishedOnHome !== undefined) formData.append('isPublishedOnHome', String(data.isPublishedOnHome));

        // Append files if provided
        if (data.image) formData.append('image', data.image);
        if (data.pdfProgramme) formData.append('pdfProgramme', data.pdfProgramme);

        const response = await api.post<Evenement>('/evenements', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return fixEvenement(response.data);
    },

    /**
     * Get all events with optional pagination
     */
    findAll: async (
        page: number = 1,
        limit: number = 100,
        status?: 'upcoming' | 'ongoing' | 'past'
    ): Promise<PaginatedEvenements | Evenement[]> => {
        const params: any = { page, limit };
        if (status) params.status = status;

        const response = await api.get<PaginatedEvenements | Evenement[]>('/evenements', {
            params,
        });

        // Handle both array and paginated responses
        if (Array.isArray(response.data)) {
            return response.data.map(fixEvenement);
        } else {
            return {
                ...response.data,
                data: response.data.data.map(fixEvenement),
            };
        }
    },

    /**
     * Get a single event by ID
     */
    findOne: async (id: string): Promise<Evenement> => {
        const response = await api.get<Evenement>(`/evenements/${id}`);
        return fixEvenement(response.data);
    },

    /**
     * Update an event with optional file re-upload
     */
    update: async (id: string, data: UpdateEvenementDto): Promise<Evenement> => {
        const formData = new FormData();

        // Append text fields if provided
        if (data.titre) formData.append('titre', data.titre);
        if (data.description !== undefined) formData.append('description', data.description);
        if (data.lienInscription !== undefined) formData.append('lienInscription', data.lienInscription);
        if (data.dateDebut) formData.append('dateDebut', data.dateDebut);
        if (data.dateFin) formData.append('dateFin', data.dateFin);
        if (data.dateSoumission) formData.append('dateSoumission', data.dateSoumission);
        if (data.isCallForPapers !== undefined) formData.append('isCallForPapers', String(data.isCallForPapers));
        if (data.isPublishedOnHome !== undefined) formData.append('isPublishedOnHome', String(data.isPublishedOnHome));

        // Append new files if provided
        if (data.image) formData.append('image', data.image);
        if (data.pdfProgramme) formData.append('pdfProgramme', data.pdfProgramme);

        const response = await api.patch<Evenement>(`/evenements/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return fixEvenement(response.data);
    },

    /**
     * Delete an event (soft delete)
     */
    delete: async (id: string): Promise<void> => {
        await api.delete(`/evenements/${id}`);
    },

    /**
     * Restore a soft-deleted event
     */
    restore: async (id: string): Promise<Evenement> => {
        const response = await api.patch<Evenement>(`/evenements/${id}/restore`);
        return fixEvenement(response.data);
    },

    /**
     * Increment view count
     */
    incrementView: async (id: string): Promise<Evenement> => {
        const response = await api.patch<Evenement>(`/evenements/${id}/view`);
        return fixEvenement(response.data);
    },

    /**
     * Increment like count
     */
    incrementLike: async (id: string): Promise<Evenement> => {
        const response = await api.patch<Evenement>(`/evenements/${id}/like`);
        return fixEvenement(response.data);
    },
};
